import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";
import { IUser } from "./useUsers";
import { ICommunity } from "./useCommunities";
import { useCurrentLocation } from "./useCurrentLocation";

export interface ITomraDropItems {
  count: number;
  type: string;
  barcode: string;
  productCode: string;
  refund: number;
  superGroup: number;
}

interface ILotteryDetails {
  _id: string;
  en_title: string;
  nl_title: string;
  isFirstTicketDrop?: boolean;
}

export interface IDrop {
  _id: string;
  status: "approved" | "declined" | "pending";
  community: string | ICommunity;
  tomraDropId: string;
  startedAt: Date;
  refund: number;
  user: string | IUser;
  items: ITomraDropItems[];
  metadata: {
    rvm: {
      serialNumber: string;
      type: string;
    };
    location: {
      customerId: string;
      name: string;
    };
  };
  joinedLottery?: ILotteryDetails[];
  rejectedLottery?: ILotteryDetails[];
  createdAt: Date;
  updatedAt: Date;
}

// interface IDropsStats {
//   approved: number;
//   declined: number;
//   pending: number;
// }

interface IDropsStats {
  tomraDropsByDays: {
    [key: string]: number;
  };
  tomraDropsByWeek: {
    [key: string]: number;
  };
  tomraDropsByMonth: {
    [key: string]: number;
  };
  tomraDropsByYear: {
    [key: string]: number;
  };
}
interface IStatieItemsPerLocation {
  name: string;
  data: [number, number][];
}

interface IStore {
  /** State */
  tomraDrops: IDrop[];
  tomraUnitStats: IDropsStats | null;
  statieItemsPerLocation: IStatieItemsPerLocation[] | null;
  statieRevenuePerLocation: IStatieItemsPerLocation[] | null;
  // dropStatusStats: IDropsStats;
  hasNextPage: boolean;
  hasNextUserTomraDrops: boolean;
  usersWithBarcodeDrops: IUser[];
  userTomraDrops: IDrop[];
  oneTomraDrop: IDrop | null;
  highlightedStatus: "Pending" | "Approved" | "Declined" | null;
  deletedTomraDrop: string | null;
  searchText: string;
  /** Tomra Drops page: location filter ("all" or location id), loading, initialized */
  tomraDropsLocationFilter: string;
  tomraDropsLoading: boolean;
  tomraDropsInitialized: boolean;
  setTomraDropsLocationFilter: (location: string) => void;
  setTomraDropsInitialized: (value: boolean) => void;
  /** Callbacks */
  getTomraUnitStats: (requestedYear?: number, requestedDay?: Date) => void;
  getStatieItemsPerLocation: (
    startYear: number,
    startValue: number,
    endYear: number,
    endValue: number,
    period?: "monthly" | "weekly",
    type?: "location" | "city",
    places?: string[]
  ) => void;
  getStatieRevenuePerLocation: (
    startYear: number,
    startValue: number,
    endYear: number,
    endValue: number,
    period?: "monthly" | "weekly",
    type?: "location" | "city",
    places?: string[]
  ) => void;
  downloadTomraDropsInCSV: () => void;
  getUsersWithBarcodeDrops: (barcode: number) => void;
  // getDropStatusStats: () => void;
  getTomraDrops: (page?: number, limit?: number, search?: string) => void;
  getUserTomraDrops: (userId: string, page?: number, limit?: number) => void;
  getOneTomraDrop: (dropId: string) => void;
  deleteTomraDrop: (dropId: string, userId?: string) => void;
  setSearchText: (searchText: string) => void;
  setHighlightedStatus: (
    status: "Pending" | "Approved" | "Declined" | null
  ) => void;
  /** Clear callbacks */
  clearUserTomraDrops: () => void;
  clearDeletedTomraDrop: () => void;
  clearSearchText: () => void;
}

export const useTomraDrops = create<IStore>()((set, get) => ({
  /** State */
  tomraDrops: [],
  usersWithBarcodeDrops: [],
  tomraUnitStats: null,
  statieItemsPerLocation: null,
  statieRevenuePerLocation: null,
  // dropStatusStats: {
  //   approved: 0,
  //   declined: 0,
  //   pending: 0,
  // },
  hasNextPage: false,
  hasNextUserTomraDrops: false,
  userTomraDrops: [],
  highlightedStatus: null,
  oneTomraDrop: null,
  createdTomraDrop: null,
  updatedTomraDrop: null,
  deletedTomraDrop: null,
  searchText: "",
  tomraDropsLocationFilter: "all",
  tomraDropsLoading: false,
  tomraDropsInitialized: false,
  setTomraDropsLocationFilter: (location) =>
    set({ tomraDropsLocationFilter: location }),
  setTomraDropsInitialized: (value) =>
    set({ tomraDropsInitialized: value }),
  /** Callbacks */
  getTomraUnitStats: async (requestedYear, requestedDay = new Date()) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `/tomra-drops/stats/units?requestedYear=${requestedYear}&requestedDay=${requestedDay}&location=${currentLocation}`
      );
      if (response?.data) {
        set({ tomraUnitStats: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching drops dashboard stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getStatieItemsPerLocation: async (
    startYear,
    startValue,
    endYear,
    endValue,
    period = "monthly",
    type?: "location" | "city",
    places?: string[]
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        endYear: endYear.toString(),
        period: period,
      });

      if (period === "monthly") {
        queryParams.append("startMonth", startValue.toString());
        queryParams.append("endMonth", endValue.toString());
      } else {
        queryParams.append("startWeek", startValue.toString());
        queryParams.append("endWeek", endValue.toString());
      }

      if (type) queryParams.append("type", type);
      if (places?.length) queryParams.append("places", places.join(","));

      const response = await axios.get(
        `tomra-drops/stats/statie-items-per-location?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ statieItemsPerLocation: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching statie items per location", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getStatieRevenuePerLocation: async (
    startYear,
    startValue,
    endYear,
    endValue,
    period = "monthly",
    type,
    places
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        startYear: startYear.toString(),
        endYear: endYear.toString(),
        period: period,
      });

      if (period === "monthly") {
        queryParams.append("startMonth", startValue.toString());
        queryParams.append("endMonth", endValue.toString());
      } else {
        queryParams.append("startWeek", startValue.toString());
        queryParams.append("endWeek", endValue.toString());
      }

      if (type) queryParams.append("type", type);
      if (places?.length) queryParams.append("places", places.join(","));

      const response = await axios.get(
        `tomra-drops/stats/statie-revenue-per-location?${queryParams.toString()}`
      );
      if (response?.data) {
        set({ statieRevenuePerLocation: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching statie revenue per location", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getUsersWithBarcodeDrops: async (barcode) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`tomra-drops/users?barcode=${barcode}`);
      if (response?.data) {
        set({ usersWithBarcodeDrops: response?.data });
      }
    } catch (error) {
      LOG.error("Error on getting users with barcode drops", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  downloadTomraDropsInCSV: async () => {
    useAppLoading.getState().setIsLoading(true);
    const location = get().tomraDropsLocationFilter;
    try {
      const response = await axios.get(
        `tomra-drops/csv?location=${location}`
      );
      if (response?.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "drops.csv");
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      LOG.error("Error on downloading drops in CSV", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  // getDropStatusStats: async () => {
  //   try {
  //     const response = await axios.get(`tomra-drops/status-stats`);
  //     if (response?.data) {
  //       set({ dropStatusStats: response.data });
  //     }
  //   } catch (error) {
  //     LOG.error("Error on fetching drop status stats", { error });
  //   }
  // },
  setHighlightedStatus: (status) => {
    set({ highlightedStatus: status });
  },
  getTomraDrops: async (page = 1, limit = 20, search) => {
    useAppLoading.getState().setIsLoading(true);
    set({ tomraDropsLoading: true });
    const location = get().tomraDropsLocationFilter;
    try {
      const highlightedStatus = get().highlightedStatus;
      const searchParam = search ?? "";
      const response = await axios.get(
        `tomra-drops?page=${page}&limit=${limit}&search=${encodeURIComponent(searchParam)}&highlightedStatus=${highlightedStatus?.toLowerCase() ?? ""}&location=${location}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPage: response?.data?.length === limit,
          tomraDrops:
            page === 1
              ? response?.data
              : [...state.tomraDrops, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on getting tomra drops", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      set({
        tomraDropsLoading: false,
        tomraDropsInitialized: true,
      });
    }
  },
  getUserTomraDrops: async (userId, page = 1, limit = 10) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `tomra-drops/user/${userId}?page=${page}&limit=${limit}&location=${currentLocation}`
      );
      if (response?.data) {
        set((state) => ({
          hasNextUserTomraDrops: response?.data?.length === limit,
          userTomraDrops:
            page === 1
              ? response?.data
              : [...state.userTomraDrops, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on getting user drops", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneTomraDrop: async (dropId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`tomra-drops/${dropId}`);
      if (response?.data) {
        set({ oneTomraDrop: response?.data });
      }
    } catch (error) {
      LOG.error("Error on getting one drop", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteTomraDrop: async (dropId, userId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`/tomra-drops/${dropId}`);
      if (response?.data) {
        set((state) => ({
          tomraDrops: state.tomraDrops.filter((drop) => drop._id !== dropId),
          deletedTomraDrop: dropId,
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting a drop", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      if (userId) {
        get().getUserTomraDrops(userId);
      }
    }
  },
  setSearchText: (searchText) => {
    set({ searchText });
  },
  /** Clear callbacks */
  clearUserTomraDrops: () => {
    set({ userTomraDrops: [] });
  },
  clearDeletedTomraDrop: () => {
    set({ deletedTomraDrop: null });
  },
  clearSearchText: () => {
    set({ searchText: "" });
  },
}));
